import type {
	BeckyFunctionLibrary,
	BeckyRequestPayload,
	OoiCssInstanceModel,
	BeckyModel,
} from '@wix/thunderbolt-becky-root'
import type { ModuleFetcher, MetricsReporter } from '@wix/thunderbolt-becky-types'
import { createCssVarsGenerator, decodeModulesToLoad } from '@wix/thunderbolt-commons'
import moduleResolverFactory from './moduleResolverFactory'
import {
	overrideStyleParams,
	shouldOverrideStyleParams,
	resolveWidgetsWithAppSettings,
} from '../styleParamsOverrides/styleParamsOverrides'
import type { Experiments } from '@wix/thunderbolt-symbols'

const resolveWidgetsToLoad = (ooiVersions: BeckyRequestPayload['ooiVersions'], widgetIdsOnPage: Array<string> = []) => {
	const allWidgetsOnSite = decodeModulesToLoad(ooiVersions || '')
	return allWidgetsOnSite.filter((w) => widgetIdsOnPage.find((widgetId) => widgetId === w.id))
}

const staticCssProcessorMemoizer = (getBuildTimeStaticCss: (cssConfig: object) => { css: string; hash?: string }) => {
	const staticCssHashes: { [hash: string]: boolean } = {}
	return (cssConfig: object) => {
		const { css, hash } = getBuildTimeStaticCss(cssConfig)
		if (!hash) {
			// backward-compatibility: cssConfig might not include the new build-time hashing feature (in tpa-webpack-plugin)
			return css
		}
		if (staticCssHashes[hash]) {
			// since staticCss already exists, we don't to return it
			return ''
		}
		staticCssHashes[hash] = true
		return css
	}
}

// todo @nitzan - align with ProcessOoiCssFunctionLib['processOoiCss']
function processOoiCssFactory({
	resolveModule,
	options: ooiWidgetsModel,
	runAndReport,
	enableOoiCssVars,
	widgetsWithAppSettings,
	isResponsive,
	isOOICssForWidgetsWithAppSettingsEnabled,
	anywhereThemeOverride,
	experiments,
}: {
	resolveModule: (id: string) => any
	options: OoiCssInstanceModel
	runAndReport: BeckyFunctionLibrary['runAndReport']
	enableOoiCssVars: boolean
	widgetsWithAppSettings: Map<string, any> | undefined
	isResponsive: boolean
	isOOICssForWidgetsWithAppSettingsEnabled: any
	anywhereThemeOverride?: string
	experiments: Experiments
}) {
	// can't use typescript import due to jest transfer exploding when looking into tpa-style-webpack-plugin (and es6 module)
	const {
		getProcessedCssWithConfig,
		getBuildTimeStaticCss,
		getProcessedCssWithVars,
		getSiteCssVariables,
		getComponentCssVariables,
	} = require('@wix/tpa-style-webpack-plugin/standalone')
	const staticCssProcessor = staticCssProcessorMemoizer(getBuildTimeStaticCss)
	const generateCssVars = createCssVarsGenerator({ getSiteCssVariables, getComponentCssVariables })
	return (
		widgetId: string,
		styleData: any,
		{ prefixSelector, withStaticCss = true }: { prefixSelector: string; withStaticCss?: boolean },
		referenceId: string | undefined,
		tpaData: any
	): string => {
		const { cssConfig, processCssVars } = resolveModule(widgetId) || {}
		const { siteColors, siteTextPresets } = styleData
		let { style: styleParams } = styleData

		if (isOOICssForWidgetsWithAppSettingsEnabled && shouldOverrideStyleParams(widgetId)) {
			styleParams = overrideStyleParams({
				styleParams,
				referenceId,
				widgetId,
				widgetsWithAppSettings,
				tpaData,
				isResponsive,
				anywhereThemeOverride,
			})
			styleData.style = styleParams
		}
		if (enableOoiCssVars) {
			return generateCssVars({
				styleData,
				prefixSelector,
				cssConfig,
				options: {
					...ooiWidgetsModel,
					dimensions: ooiWidgetsModel.dimensions[widgetId],
					usesCssPerBreakpoint: ooiWidgetsModel.usesCssPerBreakpoint[widgetId],
				},
			})
		}

		if (!processCssVars) {
			if (!cssConfig) {
				return ''
			}
			// cssConfig
			const cssConfigCss = runAndReport(
				() =>
					getProcessedCssWithConfig(
						cssConfig,
						{
							siteColors,
							siteTextPresets,
							styleParams,
						},
						{
							...ooiWidgetsModel,
							dimensions: ooiWidgetsModel.dimensions[widgetId],
							prefixSelector,
						}
					),
				`getProcessedCssWithConfig`
			)

			const staticCss = withStaticCss
				? runAndReport(() => staticCssProcessor(cssConfig), `staticCssProcessor`)
				: ''

			const cssVars = experiments['specs.thunderbolt.ooi_css_vars']
				? generateCssVars({
						styleData,
						prefixSelector,
						cssConfig,
						options: {
							...ooiWidgetsModel,
							dimensions: ooiWidgetsModel.dimensions[widgetId],
							usesCssPerBreakpoint: ooiWidgetsModel.usesCssPerBreakpoint[widgetId],
						},
				  })
				: ''
			return cssVars + staticCss + cssConfigCss
		} else {
			// else: css vars. Call the runtime file (which is replacing the '.cssConfig.' file)
			const defaults = {}
			// Css vars dynamic values, built during build time
			const { cssVarsExpressions, dynamicExpressions } = processCssVars
			const cssVarsRuntimeOutput = getProcessedCssWithVars(
				dynamicExpressions,
				cssVarsExpressions,
				{ siteColors, siteTextPresets, styleParams },
				{
					...ooiWidgetsModel,
					dimensions: ooiWidgetsModel.dimensions[widgetId],
					prefixSelector,
				},
				defaults
			)

			const cssVars = Object.keys(cssVarsRuntimeOutput).reduce((acc, key) => {
				return `${acc}--${key}:${cssVarsRuntimeOutput[key]};`
			}, '')
			return `${prefixSelector}{${cssVars}}`
		}
	}
}

export const processOoiCssImpl = async (
	{
		widegtsIds,
		widgetsWithReferenceId,
		...options
	}: {
		widegtsIds?: Array<string>
		widgetsWithReferenceId?: Array<{ widgetId: string; referenceId: string }>
	} & OoiCssInstanceModel,
	{
		funcLib: { runAsyncAndReport, runAndReport },
		moduleFetcher,
		payload,
		beckyModel,
		metricsReporter,
	}: {
		payload: Pick<BeckyRequestPayload, 'enableOoiCssVars' | 'ooiVersions'>
		funcLib: BeckyFunctionLibrary
		moduleFetcher: ModuleFetcher
		beckyModel: BeckyModel
		metricsReporter: MetricsReporter
	}
) => {
	const widgetsToLoad = resolveWidgetsToLoad(payload.ooiVersions, widegtsIds)

	const isOOICssForWidgetsWithAppSettingsEnabled =
		beckyModel.experiments['specs.thunderbolt.OOICssForWidgetsWithAppSettings']

	const widgetsWithAppSettings: Map<string, any> | undefined = isOOICssForWidgetsWithAppSettingsEnabled
		? await resolveWidgetsWithAppSettings(widgetsWithReferenceId, beckyModel.clientSpecMap, metricsReporter)
		: undefined

	const resolveModule = await moduleResolverFactory(widgetsToLoad, {
		moduleFetcher,
		runAsyncAndReport,
	})

	return processOoiCssFactory({
		resolveModule,
		options: options as OoiCssInstanceModel,
		runAndReport,
		enableOoiCssVars: payload.enableOoiCssVars === 'true',
		widgetsWithAppSettings,
		isResponsive: beckyModel.isResponsive,
		isOOICssForWidgetsWithAppSettingsEnabled,
		anywhereThemeOverride: beckyModel.anywhereThemeOverride,
		experiments: beckyModel.experiments,
	})
}
